import { Spinner } from '@studio/design-system/spinner';
// main replaced the standalone clsx/tailwind-merge deps with its own cn helper
// (utils/cn.ts, backed by shadcn-ui/cn). Using it here keeps this file building after the
// merge without re-adding a dependency main deliberately dropped; cn is varargs and
// twMerge-aware, so it is a drop-in for the conditional-class calls below.
import { cn } from '#~/utils/cn.js';
import type { ReactElement } from 'react';
import type { BridgeConnectionSteps } from '../types.js';

/**
 * Three-step bridge connection indicator adapted from RFD 0006's
 * "Detecting Bridge" prototype screen
 * (rfd/0006-bridge-connection-funnel.md, prototypes/bridge-funnel/prototype.html).
 *
 * The prototype walks 5 timer-driven steps; we collapse to the 3 we have
 * real broker/depot observations for so the indicator is honest rather
 * than scripted (RFD open question #3). Each row's status is derived from
 * the previous-step-done-and-this-step-pending rule: only the earliest
 * not-done row spins, rows after it stay dim until their predecessor
 * completes. This matches the prototype's sequential reveal feel without
 * mocking out the underlying signals.
 */
interface ConnectionStepsProps {
  readonly steps: BridgeConnectionSteps;
  /** When true, the currently-active row renders in an error tone. */
  readonly errored?: boolean;
}

interface StepDef {
  readonly key: 'agentDetected' | 'bridgeAuthenticated' | 'roomLinked';
  readonly label: string;
  readonly activeLabel: string;
  readonly erroredLabel: string;
}

// Copy aligned with RFD 0006 prototype labels but rewritten in present
// progressive ("we're ...") so the active-row labels stay calm rather than
// shouting. The prototype's "Authenticating with Iris Cloud" remains as
// the active label for the most likely real failure mode (firewall /
// expired creds) -- keeps the failure-screen copy consistent.
const STEPS: readonly StepDef[] = [
  {
    key: 'agentDetected',
    label: 'Detect your Bridge',
    activeLabel: 'Detecting your Bridge...',
    erroredLabel: 'Could not detect your Bridge'
  },
  {
    key: 'bridgeAuthenticated',
    label: 'Connect to the cloud',
    activeLabel: 'Connecting to the cloud...',
    erroredLabel: "Authentication couldn't be established"
  },
  {
    key: 'roomLinked',
    label: 'Link your Studio',
    activeLabel: 'Linking your Studio...',
    erroredLabel: 'Studio link failed'
  }
];

export const BridgeConnectionStepsIndicator = ({
  steps,
  errored = false
}: ConnectionStepsProps): ReactElement => {
  // The first not-done step is "active." Once all three are done the
  // indicator reads as fully done (the gate is about to hand off, so the
  // user shouldn't see anything spinning).
  const activeIdx = STEPS.findIndex(
    (_step, idx) => !STEPS.slice(0, idx + 1).every((prior) => steps[prior.key])
  );

  return (
    <ul className='flex w-full max-w-[28rem] flex-col gap-8' role='list'>
      {STEPS.map((step, idx) => {
        // A row shows done only when every row before it is done too. The
        // signals genuinely land out of order — the saved studio id resolves
        // instantly while the Bridge is still being detected — and an
        // indicator that ticks the LAST row while the first still spins reads
        // as broken (reported live 2026-08-25). Clamping the DISPLAY keeps the
        // sequential story without making the underlying signals lie.
        const done = STEPS.slice(0, idx + 1).every((prior) => steps[prior.key]);
        const isActive = !done && idx === activeIdx;
        const isErrored = errored && isActive;
        const isPending = !done && !isActive;

        return (
          <li
            key={step.key}
            className={cn(
              'flex items-center gap-12 rounded-6 border px-12 py-8 transition-colors',
              isErrored && 'border-error/40 bg-error/10',
              !isErrored && isActive && 'border-primary/40 bg-base-100',
              !isErrored && done && 'border-base-50 bg-base-100/50',
              !isErrored && isPending && 'border-base-50/60 bg-dark opacity-50'
            )}
          >
            <span className='flex h-20 w-20 items-center justify-center'>
              {isErrored ? (
                <ErrorGlyph />
              ) : done ? (
                <CheckGlyph />
              ) : isActive ? (
                <Spinner />
              ) : (
                <PendingDot />
              )}
            </span>
            <span
              className={cn(
                'text-sm',
                isErrored && 'text-error font-medium',
                !isErrored && isActive && 'text-white font-medium',
                !isErrored && done && 'text-foreground/75',
                !isErrored && isPending && 'text-secondary'
              )}
            >
              {isErrored ? step.erroredLabel : isActive ? step.activeLabel : step.label}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

// Inline SVGs keep this component free of design-system icon-name coupling.
// The design system's <Icon icon="..."> contract takes any string and renders
// `icon-${name}` via CSS, so we'd be guessing at a class name. SVGs are
// explicit and survive icon-pack churn.
const CheckGlyph = (): ReactElement => (
  <svg viewBox='0 0 16 16' className='h-16 w-16 text-success' fill='none' aria-hidden='true'>
    <path
      d='M3 8.5L6.5 12L13 4.5'
      stroke='currentColor'
      strokeWidth='1.75'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const ErrorGlyph = (): ReactElement => (
  <svg viewBox='0 0 16 16' className='h-16 w-16 text-error' fill='none' aria-hidden='true'>
    <path d='M4 4L12 12M12 4L4 12' stroke='currentColor' strokeWidth='1.75' strokeLinecap='round' />
  </svg>
);

const PendingDot = (): ReactElement => (
  <span className='block h-8 w-8 rounded-full bg-secondary' aria-hidden='true' />
);
